import { eng, fra, removeStopwords } from "stopword";
import { startContentAnalysis } from "./content";
import { startH1Analysis } from "./h1Tag";
import { startMeshLinksAnalysis } from "./links";
import { startMetaDataAnalysis } from "./metaData";
import { startAnalysis } from "./plan";
import { startSemanticAnalysis } from "./semantics";
import { startURLAnalysis } from "./urlSlug";
export const getText = (html) => {
  return html
    .replace(/<\/(h1|h2|h3|h4|h5|h6|p|div|br)[^>]*>/gi, "\n")
    .replace(/<\/?[^>]+(>|$)/g, "")
    .trim();
};
export const getFirstWords = (text, wordLimit) => {
  const words = text.split(/\s+/); // Split text into words using whitespace
  return words.slice(0, wordLimit).join(" "); // Take the first 'wordLimit' words and join them back into a string
};
export const cleanText = (text) => {
  return text
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&[a-z]+;/gi, "")
    .trim();
};
export const getCounts = (text) => {
  const textContent = text.trim() || "";
  return {
    chars: textContent.length,
    words: textContent.split(/\s+/).filter((word) => word.length > 0).length,
  };
};
export const removeStopWords = (text) => {
  const words = text.toLowerCase().split(/\s+/);
  const cleanedWords = removeStopwords(words, [...eng, ...fra]); // Supports English & French

  return cleanedWords.join(" ");
};
export const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const createAccentInsensitiveRegex = (input) => {
  const normalizedInput = removeAccents(input);

  return new RegExp(`\\b${normalizedInput}\\b`, "gi"); // Word boundary match
};

export const findMatches = (text, input) => {
  const normalizedText = removeAccents(text); // Remove accents from text

  const regex = createAccentInsensitiveRegex(input);

  return normalizedText.match(regex);
};

export const computeScore = (values, percentage) => {
  const score = values.reduce((sum, current) => sum + current, 0) * percentage;

  return score === -1 ? null : score;
};

export const computeCorrectScore = (values, weights) => {
  if (values.length !== weights.length) {
    throw new Error("Values and weights arrays must have the same length.");
  }

  let totalWeight = 0;
  let weightedSum = 0;

  values.forEach((value, index) => {
    const validValue = value === -1 ? null : value;
    if (validValue !== null) {
      weightedSum += validValue * weights[index];
      totalWeight += weights[index];
    }
  });

  return totalWeight > 0 ? weightedSum / totalWeight : null;
};

export const getColor = (percentage) => {
  if (percentage <= 35) return "from-red-600 from-0% to-red-600 to-100%";
  if (percentage <= 70) return "from-red-600 from-0% to-yellow-400 to-100%";
  return "from-red-600 from-0% via-yellow-400 via-70% to-green-500 to-100%";
};

export const CircularGauge = ({ percentage, isEvenRow }) => {
  const getColor = (percentage) => {
    if (percentage <= 35) return "text-red-600 border-red-600";
    if (percentage <= 70) return "text-yellow-400 border-yellow-400";
    return "text-green-500 border-green-500";
  };

  return (
    <div className="relative w-12 h-12 flex items-center justify-center">
      <svg className="w-full h-full rotate-[-90deg]">
        <circle
          cx="50%"
          cy="50%"
          r="18"
          stroke={isEvenRow ? "#ffffff" : "#e5e7eb"} // Invert based on row
          strokeWidth="4"
          fill="transparent"
        />
        <circle
          cx="50%"
          cy="50%"
          r="18"
          stroke="currentColor"
          strokeWidth="4"
          strokeDasharray={`${Math.PI * 2 * 18}`}
          strokeDashoffset={`${Math.PI * 2 * 18 * (1 - percentage / 100)}`}
          fill="transparent"
          className={getColor(percentage)}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
        {percentage}%
      </span>
    </div>
  );
};

export const globalScore = (formikProps, competitorResults) => {
  const analysisResults = [
    { name: "plan", value: startAnalysis(formikProps) },
    { name: "url", value: startURLAnalysis(formikProps) },
    { name: "title", value: startH1Analysis(formikProps, competitorResults) },
    {
      name: "content",
      value: startContentAnalysis(formikProps, competitorResults),
    },
    { name: "metaData", value: startMetaDataAnalysis(formikProps) },
    {
      name: "semantics",
      value: startSemanticAnalysis(formikProps, competitorResults),
    },
    {
      name: "linking",
      value: startMeshLinksAnalysis(formikProps, competitorResults),
    },
  ];
  const percentage = 1 / analysisResults.length;

  const totalScore = Math.round(
    computeScore(
      analysisResults
        .filter((x) => x.value.total_score)
        .map((x) => x.value.total_score),
      percentage
    )
  );

  let result = analysisResults.reduce(
    (acc, item) => {
      acc[item.name] = item.value.total_score;
      return acc;
    },
    { total_score: totalScore }
  );
  return result;
};
export const getSemanticTranslation = (t, name) => {
  const names = {
    Introduction: t("the-introduction", "l'introduction"),
    H2: t("a-H2", "a H2"),
    Conclusion: t("the-conclusion", "la conclusion"),
  };
  return names[name];
};

export const getTags = (html) => {
  const matches = [
    ...html.matchAll(/<(table|ul|ol|blockquote|p|h\d)[^>]*>(.*?)<\/\1>/gs),
  ]
    .map((match) => {
      const tag = match[1];
      let content = match[2];

      const emphasisMatches = [
        ...content.matchAll(/<(strong|em)[^>]*>(.*?)<\/\1>/gs),
      ].map((emphMatch) => ({
        tag: emphMatch[1],
        content: cleanText(emphMatch[2]),
      }));

      if (tag === "ul" || tag === "ol") {
        const listItems = [...content.matchAll(/<li[^>]*>(.*?)<\/li>/gs)].map(
          (liMatch) => cleanText(liMatch[1])
        );
        content = cleanText(listItems.join(" "));
      } else if (tag === "table") {
        const rows = [...content.matchAll(/<tr[^>]*>(.*?)<\/tr>/gs)].map(
          (row) => {
            const cells = [
              ...row[1].matchAll(/<t[hd][^>]*>(.*?)<\/t[hd]>/gs),
            ].map((cell) => cleanText(cell[1]));
            return cells.join(" ");
          }
        );
        content = cleanText(rows.join("\n"));
      } else {
        content = cleanText(content.replace(/<[^>]+>/g, ""));
      }

      return {
        tag,
        content,
        emphasis: emphasisMatches.length > 0 ? emphasisMatches : null,
        sentences: content
          .split(/[.!?]+/)
          .filter((sentence) => sentence.trim().length > 0).length,
      };
    })
    .filter((item) => item.content.length > 0);

  return matches;
};
